/**
 * UI sounds, synthesised.
 *
 * Generated with the Web Audio API rather than shipped as files: nothing to
 * download, nothing to cache, and they work in the dead spots on a course where
 * an asset fetch would fail.
 *
 * Kept short and quiet. A sound that plays every time you touch something stops
 * being feedback and becomes noise.
 */

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const C =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return null;
    ctx ??= new C();
    // iOS suspends the context until a gesture resumes it.
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/** Must be called from a tap, once, before any sound will play on iOS. */
export function unlockSounds() {
  audio();
}

interface ToneSpec {
  from: number;
  to: number;
  ms: number;
  gain?: number;
  type?: OscillatorType;
}

function tone({ from, to, ms, gain = 0.09, type = 'sine' }: ToneSpec, delayMs = 0) {
  const a = audio();
  if (!a) return;
  const t0 = a.currentTime + delayMs / 1000;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(from, t0);
  osc.frequency.exponentialRampToValueAtTime(Math.max(to, 1), t0 + ms / 1000);
  // Short attack and a real decay — an abrupt stop clicks.
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + ms / 1000);
  osc.connect(g).connect(a.destination);
  osc.start(t0);
  osc.stop(t0 + ms / 1000 + 0.02);
}

/** Rising two-note blip: listening has started. */
export function soundListenStart() {
  tone({ from: 520, to: 760, ms: 110 });
  tone({ from: 760, to: 980, ms: 120 }, 90);
}

/** Falling blip: stopped listening without acting. */
export function soundListenCancel() {
  tone({ from: 620, to: 380, ms: 150, gain: 0.07 });
}

/**
 * The Waze "working on it" chirp — a quick rising triplet played while the
 * command is being resolved.
 */
export function soundCalculating() {
  tone({ from: 440, to: 560, ms: 90, gain: 0.07 });
  tone({ from: 560, to: 700, ms: 90, gain: 0.07 }, 80);
  tone({ from: 700, to: 900, ms: 110, gain: 0.08 }, 160);
}

/** Command understood and applied. */
export function soundConfirm() {
  tone({ from: 720, to: 940, ms: 90, gain: 0.08 });
  tone({ from: 940, to: 1180, ms: 130, gain: 0.08 }, 85);
}

/** Command not understood. Deliberately soft — being told off is unpleasant. */
export function soundReject() {
  tone({ from: 400, to: 280, ms: 200, gain: 0.06, type: 'triangle' });
}
